import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import Dropzone from "react-dropzone";
import { humanFileSize } from "../../store/formatUtil";
import { Delete } from "@mui/icons-material";
import { getFileType } from "../../store/parseUtil";

export default function FileDropzone({ fileList, filesAdded, fileRemoved }) {
  const theme = useTheme();

  const onFileClick = (e) => {
    e.stopPropagation();
  };

  const onDeleteClick = (e, f) => {
    e.stopPropagation();
    if (fileRemoved) fileRemoved(f);
  };

  const isJSONFile = (f) =>
    (f.path || f.name || "").toUpperCase().endsWith(".JSON");

  const readJSONFile = async (f) => {
    console.log("PARSING JSON FILE: " + f.path);
    try {
      return JSON.parse(await f.text());
    } catch (e) {
      console.error("Failed to parse JSON file: " + f.path, e);
      return null;
    }
  };

  // The parsed JSON is handed off with the audio files rather than applied here,
  // so the caller can apply it after the audio files are queued. A JSON dropped
  // alongside a .int16 has nothing to describe until the audio is in the queue.
  const onDrop = async (files) => {
    const droppedFiles = files.filter((f) => !isJSONFile(f));
    const jsonData = (
      await Promise.all(files.filter(isJSONFile).map(readJSONFile))
    ).filter((o) => o !== null);

    if (filesAdded) filesAdded(droppedFiles, jsonData);
  };

  return (
    <Box display="flex" flex={1}>
      <Dropzone
        onDrop={onDrop}
        useFsAccessApi={false}
        sx={{ display: "flex", flex: 1, maxHeight: 300 }}
      >
        {({ getRootProps, getInputProps }) => (
          <Box
            {...getRootProps()}
            sx={{
              cursor: "pointer",
              width: "100%",
              display: "flex",
              flex: 1,
              border: "dashed",
              borderRadius: 1,
              borderWidth: 1,
              borderColor: theme.palette.grey.main,
            }}
            style={{ minHeight: 400, height: "100%", width: "100%" }}
          >
            <input {...getInputProps()} />
            <List sx={{ flex: 1, overflow: "auto" }}>
              {fileList.map((data) => {
                const { fileData } = data;

                return (
                  <ListItem
                    sx={{
                      border: 0,
                      borderBottom: 1,
                      borderStyle: "solid",
                      borderColor: theme.palette.grey.light,
                    }}
                    id={data.path}
                    onClick={onFileClick}
                    key={data.path}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        id={data.path}
                        onClick={(e) => {
                          onDeleteClick(e, data);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={data.path}
                      secondary={humanFileSize(data.size, true)}
                      sx={{
                        "&.MuiListItemText-root": {
                          flex: 1,
                          mr: 2,
                          overflow: "hidden",
                          "& .MuiTypography-root": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        },
                      }}
                    />
                    {getFileType(data.name) !== "INT16" &&
                      (fileData.channels > 1 ? (
                        <ListItemText
                          secondary={`Channels 1-${fileData.channels}`}
                        />
                      ) : (
                        <ListItemText secondary={`Channel ${data.channel}`} />
                      ))}
                  </ListItem>
                );
              })}

              <ListItem key="instructions">
                <Typography
                  variant="h6"
                  style={{
                    color: theme.palette.primary.main,
                    margin: "auto",
                    paddingTop: 16,
                  }}
                >
                  Drop files or click to select from computer
                </Typography>
              </ListItem>
            </List>
          </Box>
        )}
      </Dropzone>
    </Box>
  );
}
